import type { ParentComponent} from 'solid-js';
import { splitProps } from 'solid-js';
import type { StyleSafeComponentProps } from '@deltablue/ts-utils';
import c from './Table.module.css';

type TableType = {
    Donut: ParentComponent<StyleSafeComponentProps<'table'>>,
    /** Block for table header, only place directly inside Table.Donut */
    Thead: ParentComponent<StyleSafeComponentProps<'thead'>>,
    /** Block for table body, only place directly inside Table.Donut */
    Tbody: ParentComponent<StyleSafeComponentProps<'tbody'>>,
    /** Block for table row, only place directly inside Table.Thead and Table.Tbody */
    Tr: ParentComponent<StyleSafeComponentProps<'tr'>>,
    /** Block for table header cell, only place directly inside Table.Tr */
    Th: ParentComponent<StyleSafeComponentProps<'th'> & {
        grow?: boolean;
        minWidth?: string;
    }>,
    /** Block for table cell, only place directly inside Table.Tr */
    Td: ParentComponent<StyleSafeComponentProps<'td'> & {
        grow?: boolean;
        noWrap?: boolean;
        verticalAlign?: 'top' | 'middle' | 'baseline';
    }>,
    /** Block for table cell with fallback content, only place directly inside Table.Donut. Meant to have a colspan that stretches the entire table. */
    Fallback: ParentComponent<{ span: number; }>,
    /** Block for table cell with a way to add a new item to a list, only place directly inside Table.Tr. Meant to have a colspan that stretches the entire table. */
    Add: ParentComponent<{ span: number; onClick: () => void; }>,
};

export const Table: TableType = {
    Donut: p => (
        <div class={c.container}>
            <table class={c.table} {...p} />
        </div>
    ),
    Thead: p => (
        <thead
            class={c.thead}
            {...p}
        />
    ),
    Th: p => {
        const [, o] = splitProps(p, ['grow', 'minWidth']);
        return (
            <th
                style={{ '--minWidth': p.minWidth }}
                class={c.th}
                classList={{ [c.grow]: p.grow }}
                {...o}
            />
        );
    },
    Tbody: p => (
        <tbody
            class={c.tbody}
            {...p}
        />
    ),
    Tr: p => (
        <tr
            class={c.tr}
            {...p}
        />
    ),
    Td: p => {
        const [, o] = splitProps(p, ['noWrap', 'verticalAlign']);
        return (
            <td
                class={c.td}
                classList={{
                    [c.noWrap]: p.noWrap,
                    [c.grow]: p.grow,
                }}
                style={{
                    '--verticalAlign': p.verticalAlign ?? 'middle',
                }}
                {...o}
            />
        );
    },
    Fallback: p => (
        <tr class={c.tr}>
            <td
                class={[c.td, c.fallback].join(' ')}
                colSpan={p.span}
            >
                {p.children}
            </td>
        </tr>
    ),
    Add: p => (
        <tr class={c.tr}>
            <td
                class={[c.td, c.add].join(' ')}
                colSpan={p.span}
            >
                <button
                    class={c.addBtn}
                    onClick={p.onClick}
                    type='button'
                >
                    +
                </button>
            </td>
        </tr>
    ),
};
